import { useAuth } from '@clerk/clerk-react';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { API_BASE_URL } from './client.js';

// Connects to the API's WebSocket endpoint, keeps the session alive with pings, and
// invalidates React Query caches when server-side events arrive so the visible data
// reflects them immediately.
//
// Reconnect strategy: exponential backoff from 1s → 30s. We never stop trying — if the
// API is down we'll reconnect once it comes back. Unmounting the component that mounts
// the hook tears the connection down cleanly.

type WsEvent = { type: string; data?: unknown; ts?: string; seq?: number };
type Status = 'idle' | 'connecting' | 'open' | 'closed' | 'error';

const wsBase = (() => {
  // http(s) → ws(s) for the same origin
  return API_BASE_URL.replace(/^http/, 'ws');
})();

export function useLiveEvents() {
  const { getToken, isSignedIn } = useAuth();
  const qc = useQueryClient();
  const [status, setStatus] = useState<Status>('idle');
  const socketRef = useRef<WebSocket | null>(null);
  const cancelledRef = useRef(false);
  const reconnectAttempts = useRef(0);
  // Tracks the highest `seq` we've observed. On reconnect we pass `?since_seq=<n>` so
  // the server replays anything published during the outage (up to the 5-min buffer).
  const lastSeqRef = useRef<number | null>(null);

  useEffect(() => {
    cancelledRef.current = false;
    if (!isSignedIn) return;

    async function connect() {
      if (cancelledRef.current) return;
      setStatus('connecting');

      const token = await getToken();
      if (!token || cancelledRef.current) return;

      const qs = new URLSearchParams({ token });
      if (lastSeqRef.current !== null) qs.set('since_seq', String(lastSeqRef.current));
      const url = `${wsBase}/ws?${qs.toString()}`;
      const socket = new WebSocket(url);
      socketRef.current = socket;

      const pingInterval = window.setInterval(() => {
        if (socket.readyState === WebSocket.OPEN) {
          try {
            socket.send(JSON.stringify({ type: 'ping' }));
          } catch {
            /* socket closed between check and send */
          }
        }
      }, 30_000);

      socket.onopen = () => {
        reconnectAttempts.current = 0;
        setStatus('open');
      };

      socket.onmessage = (evt) => {
        let msg: WsEvent;
        try {
          msg = JSON.parse(evt.data as string);
        } catch {
          return;
        }
        if (typeof msg.seq === 'number') {
          lastSeqRef.current = Math.max(lastSeqRef.current ?? 0, msg.seq);
        }
        if (msg.type === 'connection.replay_gap') {
          // We missed events that fell out of the server's buffer. Refetch everything
          // tied to this page so the UI catches up from REST.
          qc.invalidateQueries();
          return;
        }
        // Lifecycle events → matching cache invalidations. The server response is
        // authoritative and affected lists are cheap to re-pull. Roster rows carry
        // active_alarm_id and active_check_in_id columns, so alarm and check-in events
        // also invalidate the roster query.
        if (msg.type === 'alarm.created' || msg.type === 'alarm.updated') {
          qc.invalidateQueries({ queryKey: ['alarms'] });
          qc.invalidateQueries({ queryKey: ['roster'] });
        } else if (msg.type === 'check_in.created' || msg.type === 'check_in.updated') {
          qc.invalidateQueries({ queryKey: ['check-ins'] });
          qc.invalidateQueries({ queryKey: ['roster'] });
        } else if (msg.type === 'location.updated') {
          qc.invalidateQueries({ queryKey: ['locations'] });
        } else if (msg.type === 'broadcast.sent') {
          qc.invalidateQueries({ queryKey: ['broadcasts'] });
        }
      };

      socket.onclose = (evt) => {
        clearInterval(pingInterval);
        setStatus('closed');
        if (cancelledRef.current) return;
        // 4001/4002/4003 are auth failures — don't loop on those, they require a new
        // token from Clerk which useAuth refreshes on its own cadence. Other closes get
        // a backoff retry.
        const authFailure = evt.code >= 4001 && evt.code <= 4003;
        if (authFailure) return;
        const delay = Math.min(1000 * 2 ** reconnectAttempts.current, 30_000);
        reconnectAttempts.current += 1;
        setTimeout(connect, delay);
      };

      socket.onerror = () => {
        setStatus('error');
        socket.close();
      };
    }

    connect();

    return () => {
      cancelledRef.current = true;
      const sock = socketRef.current;
      socketRef.current = null;
      if (sock && sock.readyState <= WebSocket.OPEN) sock.close(1000, 'component unmount');
    };
  }, [getToken, isSignedIn, qc]);

  return { status };
}
